import { html, PropertyValues, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { UserAnnotation, userAnnotationState } from "@/state/UserAnnotations";
import { AnnotationForm } from "@/components/annotations/annotation_form";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import "@/components/saved_annotations/new_saved_annotation";
import { initTooltips, triggerKaTeX } from "@/utilities";
import "@/components/saved_annotations/saved_annotation_icon";
import { annotationState } from "@/state/Annotations";
import { savedAnnotationState } from "@/state/SavedAnnotations";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import * as Sentry from "@sentry/browser";

/**
 * This component represents a single user annotation.
 * It can be either a root annotation or a response.
 * It also contains the form for editing the annotation.
 *
 * @element d-user-annotation
 *
 * @prop {UserAnnotation} data - The annotation
 * @prop {boolean} editing - Whether the annotation is being edited (Internal)
 */
@customElement("d-user-annotation")
export class UserAnnotationComponent extends DodonaElement {
    @property({ type: Object })
    data: UserAnnotation;

    @property({ state: true })
    editing = false;

    annotationFormRef: Ref<AnnotationForm> = createRef();

    private lastRenderedMarkdown = "";

    get headerText(): string {
        if (!this.data.permission.can_see_annotator) {
            return I18n.t("js.user_annotation.anonymous_message");
        }

        const timestamp = I18n.formatDate(this.data.created_at, "time.formats.annotation");
        const user = this.data.user?.name;

        return I18n.t("js.user_annotation.meta", { user: user, time: timestamp });
    }

    get type(): string {
        return annotationState.isQuestionMode ? "user_question" : "user_annotation";
    }

    protected get header(): TemplateResult {
        return html`
            <span class="annotation-meta-text">${this.headerText}</span>

            ${
                !this.data.released
                    ? this.renderMetaIcon("mdi-eye-off", I18n.t("js.user_annotation.not_released"))
                    : html``
            }

            <d-saved-annotation-icon .savedAnnotationId=${this.data.saved_annotation_id ?? null}>
            </d-saved-annotation-icon>

            ${
                this.data.newer_submission_url
                    ? html`
                          <span>
                              ·
                              <a
                                  href=${this.data.newer_submission_url}
                                  target="_blank"
                                  aria-label=${I18n.t("js.user_question.has_newer_submission")}
                                  title=${I18n.t("js.user_question.has_newer_submission")}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                              >
                                  <i class="mdi mdi-information mdi-18 colored-info" aria-hidden="true"></i>
                                  <span class="visually-hidden">
                                      ${I18n.t("js.user_question.has_newer_submission")}
                                  </span>
                              </a>
                          </span>
                      `
                    : html``
            }
            ${
                this.data.question_state === "unanswered"
                    ? this.renderMetaIcon(
                          "mdi-comment-question-outline",
                          I18n.t("js.user_question.is_unanswered"),
                          "colored-secondary",
                      )
                    : html``
            }
            ${
                this.data.question_state === "in_progress"
                    ? this.renderMetaIcon("mdi-comment-processing-outline", I18n.t("js.user_question.is_in_progress"))
                    : html``
            }
            ${
                this.data.question_state === "answered"
                    ? this.renderMetaIcon("mdi-comment-check-outline", I18n.t("js.user_question.is_answered"))
                    : html``
            }
        `;
    }

    deleteAnnotation(): void {
        // i18n-tasks-use t('js.user_annotation.delete_confirm') t('js.user_question.delete_confirm')
        if (confirm(I18n.t(`js.${this.type}.delete_confirm`))) {
            void userAnnotationState.delete(this.data);
        }
    }

    async updateAnnotation(e: CustomEvent): Promise<void> {
        try {
            await userAnnotationState.update(this.data, {
                annotation_text: e.detail.text,
                saved_annotation_id: e.detail.savedAnnotationId || undefined,
            });
            if (e.detail.saveAnnotation) {
                await savedAnnotationState.create({
                    from: this.data.id,
                    saved_annotation: {
                        title: e.detail.savedAnnotationTitle,
                        annotation_text: e.detail.text,
                    },
                });
            }
            this.editing = false;
        } catch {
            const form = this.annotationFormRef.value;
            form.hasErrors = true;
            form.disabled = false;
        }
    }

    protected updated(): void {
        if (!this.editing) {
            const md = this.data?.rendered_markdown ?? "";
            if (md !== this.lastRenderedMarkdown) {
                triggerKaTeX(this);
                this.lastRenderedMarkdown = md;
            }
        }
        // Reinitialize tooltips
        initTooltips(this);
    }

    onMouseEnter(): void {
        this.data.isHovered = true;
    }

    onMouseLeave(): void {
        this.data.isHovered = false;
    }

    onFocusIn(): void {
        this.data.isHovered = true;
    }

    onFocusOut(e: FocusEvent): void {
        // Avoid flicker when focus moves between elements inside the annotation (kebab button → menu item, etc.)
        const next = e.relatedTarget as Node | null;
        if (next && this.contains(next)) return;
        this.data.isHovered = false;
    }

    onEditClick(): void {
        this.editing = true;
    }

    onDeleteClick(): void {
        this.deleteAnnotation();
    }

    onReopenClick(): void {
        this.reopenQuestion();
    }

    onCancelEdit(): void {
        this.editing = false;
    }

    onSubmitEdit(e: CustomEvent): void {
        // `void` intentionally ignores the Promise (and keeps linters happy about “floating” promises)
        void this.updateAnnotation(e);
    }

    reopenQuestion(): void {
        void userAnnotationState.transition(this.data, "unanswered");
    }

    private get dropdownOptions(): TemplateResult[] {
        const options: TemplateResult[] = [];

        if (this.data.permission.update) {
            // i18n-tasks-use t('js.user_annotation.edit') t('js.user_question.edit')
            options.push(html`
                <li>
                    <button type="button" class="dropdown-item" @click=${this.onEditClick}>
                        <i class="mdi mdi-pencil mdi-18" aria-hidden="true"></i>
                        ${I18n.t(`js.${this.type}.edit`)}
                    </button>
                </li>
            `);
        }
        if (this.data.permission.save && !this.data.saved_annotation_id) {
            options.push(html`
                <li>
                    <d-new-saved-annotation
                        class="dropdown-item"
                        from-annotation-id=${String(this.data.id)}
                        annotation-text=${this.data.annotation_text}
                        role="button"
                        tabindex="0"
                    >
                    </d-new-saved-annotation>
                </li>
            `);
        }
        if (this.data.permission.destroy) {
            options.push(html`
                <li>
                    <button type="button" class="dropdown-item" @click=${this.onDeleteClick}>
                        <i class="mdi mdi-delete mdi-18" aria-hidden="true"></i>
                        ${I18n.t("js.user_annotation.delete")}
                    </button>
                </li>
            `);
        }
        if (this.data.permission.transition?.unanswered) {
            options.push(html`
                <li>
                    <button type="button" class="dropdown-item" @click=${this.onReopenClick}>
                        <i class="mdi mdi-comment-question-outline mdi-18" aria-hidden="true"></i>
                        ${I18n.t("js.user_question.unresolve")}
                    </button>
                </li>
            `);
        }

        return options;
    }

    private renderMetaIcon(icon: string, text: string, extraClasses = ""): TemplateResult {
        return html`
            <span class="annotation-meta-icon-wrap">
                <i
                    class="mdi ${icon} mdi-18 annotation-meta-icon ${extraClasses}"
                    aria-hidden="true"
                    title=${text}
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                ></i>
                <span class="visually-hidden">${text}</span>
            </span>
        `;
    }

    render(): TemplateResult {
        const options = this.dropdownOptions;
        const menuId = `annotation-options-${this.data.id}`;
        const toggleId = `annotation-options-toggle-${this.data.id}`;

        return html`
            <div
                class="annotation ${this.data.type === "annotation" ? "user" : "question"}"
                @mouseenter=${this.onMouseEnter}
                @mouseleave=${this.onMouseLeave}
                @focusin=${this.onFocusIn}
                @focusout=${this.onFocusOut}
            >
                <div class="annotation-header">
                    <span class="annotation-meta">${this.header}</span>
                    ${
                        options.length > 0
                            ? html`
                                  <div class="dropdown actions float-end kebab-menu">
                                      <button
                                          id=${toggleId}
                                          type="button"
                                          class="btn btn-icon btn-icon-muted dropdown-toggle"
                                          data-bs-toggle="dropdown"
                                          aria-haspopup="true"
                                          aria-controls=${menuId}
                                          aria-expanded="false"
                                          aria-label=${I18n.t("js.user_annotation.menu")}
                                      >
                                          <i class="mdi mdi-dots-vertical text-muted" aria-hidden="true"></i>
                                      </button>
                                      <ul
                                          id=${menuId}
                                          class="dropdown-menu dropdown-menu-end"
                                          aria-labelledby=${toggleId}
                                      >
                                          ${options}
                                      </ul>
                                  </div>
                              `
                            : html``
                    }
                </div>

                ${
                    this.editing
                        ? html`
                              <d-annotation-form
                                  annotation-text=${this.data.annotation_text}
                                  saved-annotation-id=${this.data.saved_annotation_id ?? ""}
                                  @cancel=${this.onCancelEdit}
                                  @submit=${this.onSubmitEdit}
                                  ${ref(this.annotationFormRef)}
                                  submit-button-text="update"
                              ></d-annotation-form>
                          `
                        : html`<div class="annotation-text">${unsafeHTML(this.data.rendered_markdown)}</div>`
                }
            </div>
        `;
    }
}
